import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { Eye } from 'lucide-react'
import CampaignWorkspace from '@/components/CampaignWorkspace'
import EventFormRichSections, { type RichSectionKey } from '@/components/EventFormRichSections'
import SavedDialog from '@/components/SavedDialog'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { TabsContent } from '@/components/ui/tabs'
import CampaignEditorTabs, { type EditTab } from '@/components/event-form/CampaignEditorTabs'
import {
  AppearanceSection,
  CoverImageSection,
  DateVenueSection,
  EventSettingsSection,
  FundraisingSection,
  GeneralInfoSection,
  PageTypeSection,
} from '@/components/event-form/CoreSections'
import { PreviewButton, PreviewDialog } from '@/components/event-form/PreviewDialog'
import SellingSections, { type SellingTab } from '@/components/event-form/SellingSections'
import TicketingPaymentsSection from '@/components/event-form/TicketingPaymentsSection'
import {
  buildPurchasableItemsAttributes,
  formatDateForInput,
  formatTimeForInput,
  newPurchasableItemRow,
  transformForPreview,
  type EventFormEventData,
  type EventFormPurchasableItem,
  type PurchasableItemRowState,
} from '@/components/event-form/shared'
import { serializeSponsor, withSponsorClientId } from '@/lib/sponsors'
import type { Event, SharedProps } from '@/types'

export type { EventFormPurchasableItem } from '@/components/event-form/shared'

type EventFormProps = {
  event?: Event
  dressCodes: string[]
  causes: string[]
  actionUrl?: string
  isAdmin?: boolean
  initialPageKind?: 'event' | 'fundraiser'
  initialPaymentMode?: 'native' | 'external'
  initialExternalUrl?: string | null
  purchasableItems?: EventFormPurchasableItem[]
  stripePaymentReady?: boolean
  landingUrl?: string | null
}

export default function EventForm({
  event,
  dressCodes,
  causes,
  actionUrl,
  isAdmin = false,
  initialPageKind = 'event',
  initialPaymentMode = 'native',
  initialExternalUrl = null,
  purchasableItems = [],
  stripePaymentReady = false,
  landingUrl = null,
}: EventFormProps) {
  const isEdit = !!event
  // Org editors preview via the real public page in a new tab (save-then-open); create
  // and admin keep the in-page modal preview.
  const useFullPagePreview = isEdit && !isAdmin
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(event?.cover_image_url ?? null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [savedOpen, setSavedOpen] = useState(false)
  const submitStatus = useRef<'published' | null>(null)
  const [purchasableItemRows, setPurchasableItemRows] = useState<PurchasableItemRowState[]>(
    () => buildPurchasableItemRows(purchasableItems),
  )
  const { current_organization } = usePage<SharedProps>().props
  const isPro = isAdmin || (current_organization?.pro ?? false)

  const { data, setData, post, patch, processing, errors, transform } = useForm<{ event: EventFormEventData }>({
    event: {
      page_kind: event?.page_kind ?? initialPageKind,
      title: event?.title ?? '',
      description: event?.description ?? '',
      date: formatDateForInput(event?.date),
      start_time: formatTimeForInput(event?.start_time ?? null),
      end_time: formatTimeForInput(event?.end_time ?? null),
      venue_name: event?.venue_name ?? '',
      street_address: event?.street_address ?? '',
      city: event?.city ?? '',
      state: event?.state ?? '',
      zip: event?.zip ?? '',
      dress_code: event?.dress_code ?? '',
      starting_ticket_price: event?.starting_ticket_price ?? '',
      ticket_link: event?.ticket_link ?? initialExternalUrl ?? '',
      purchase_button_text: event?.purchase_button_text ?? '',
      payment_mode: event?.payment_mode ?? initialPaymentMode,
      auction_url: event?.auction_url ?? '',
      donation_url: event?.donation_url ?? '',
      raffle_url: event?.raffle_url ?? '',
      cover_image: null,
      remove_cover_image: false,
      hashtags_input: (event?.hashtags ?? []).join(', '),
      primary_cause: event?.primary_cause ?? '',
      causes: event?.causes ?? [],
      countdown_timer: event?.countdown_timer ?? false,
      auction_items: event?.auction_items ?? false,
      gift_items: event?.gift_items ?? false,
      donations_enabled: event?.donations_enabled ?? false,
      show_donation_progress: event?.show_donation_progress ?? false,
      donation_goal_dollars: event?.donation_goal_dollars ?? '',
      status: event?.status ?? 'draft',
      visibility: event?.visibility ?? 'public',
      template_style: event?.template_style ?? 'classic',
      background_color: event?.background_color ?? 'default',
      hosts: event?.hosts ?? [],
      honorees: event?.honorees ?? [],
      entertainment: event?.entertainment ?? [],
      sponsors: (event?.sponsors ?? []).map((sponsor) => ({
        ...withSponsorClientId(sponsor),
        featured: sponsor.featured ?? false,
        logo: sponsor.logo ?? null,
      })),
      committee_members: event?.committee_members ?? [],
    },
  })

  const pageErrors = usePage().props.errors as Record<string, string | string[]> | undefined
  const isFundraiser = data.event.page_kind === 'fundraiser'
  const isExternal = data.event.payment_mode === 'external'
  const showTicketingSection = !isFundraiser
  const showNativeItemBuilder = !isAdmin && !isFundraiser
  // Native selling (sponsorships/ads/donations tabs) only applies in native mode.
  const showNativeSelling = showNativeItemBuilder && !isExternal
  const tabbedEdit = isEdit && !isAdmin
  const [activeEditTab, setActiveEditTab] = useState<EditTab>('details')
  const [activeSellingTab, setActiveSellingTab] = useState<SellingTab>('sponsorships')

  useEffect(() => {
    if (!tabbedEdit) return

    const syncTabToHash = () => {
      const hash = window.location.hash.replace('#', '') as EditTab
      const allowedTabs: EditTab[] = isFundraiser
        ? ['details', 'appearance']
        : ['details', 'appearance', 'hosts', 'entertainment', 'links', 'selling', 'sponsors']
      if (allowedTabs.includes(hash)) setActiveEditTab(hash)
    }

    syncTabToHash()
    window.addEventListener('hashchange', syncTabToHash)
    return () => window.removeEventListener('hashchange', syncTabToHash)
  }, [tabbedEdit, isFundraiser])

  useEffect(() => {
    if (!pageErrors) return

    const nextSellingTab = showNativeItemBuilder
      ? getSellingTabForErrors(pageErrors, purchasableItemRows, activeSellingTab)
      : null
    if (nextSellingTab) {
      setActiveSellingTab(nextSellingTab)
      if (tabbedEdit) {
        setActiveEditTab('selling')
        window.history.replaceState(null, '', '#selling')
      }
    }

    const erroredIds = new Set(
      Object.keys(pageErrors)
        .map((key) => /^purchasable_items\.([^.]+)\.base$/.exec(key)?.[1])
        .filter((id): id is string => !!id),
    )
    if (erroredIds.size === 0) return
    setPurchasableItemRows((rows) => rows.map((row) => (
      row.destroyed && erroredIds.has(row.id) ? { ...row, destroyed: false } : row
    )))
  }, [pageErrors, showNativeItemBuilder, tabbedEdit])

  function setEvent(next: EventFormEventData) {
    setData('event', next)
  }

  function fieldError(field: string, tierId?: string): string | undefined {
    if (tierId) {
      const pageErr = pageErrors?.[`purchasable_items.${tierId}.${field}`]
      return Array.isArray(pageErr) ? pageErr[0] : pageErr
    }

    const formErr = errors[`event.${field}` as keyof typeof errors] as string | undefined
    if (formErr) return formErr
    const pageErr = pageErrors?.[field]
    return Array.isArray(pageErr) ? pageErr[0] : pageErr
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    transform((formData) => ({ event: buildPayload(formData.event) }))
    const options: Parameters<typeof patch>[1] = { onFinish: () => { submitStatus.current = null } }
    if (isEdit) {
      // Org editors stay on the page and get a confirmation dialog; admin/create keep their redirects.
      if (!isAdmin) {
        options.preserveScroll = true
        options.preserveState = true
        options.onSuccess = (page) => {
          const nextItems = (page.props as { purchasable_items?: EventFormPurchasableItem[] }).purchasable_items
          if (nextItems) setPurchasableItemRows(buildPurchasableItemRows(nextItems))
          setSavedOpen(true)
        }
      }
      patch(actionUrl ? `${actionUrl}/${event.id}` : `/dashboard/events/${event.id}`, options)
    } else {
      post(actionUrl || '/dashboard/events', options)
    }
  }

  // Save the current edits, then open the real public event page in the pre-opened tab.
  // The tab is opened synchronously in the click handler so the browser treats it as a
  // user gesture and doesn't block the popup.
  function handlePreview() {
    if (!event) return
    const previewWindow = window.open('about:blank', '_blank')
    transform((formData) => ({ event: buildPayload(formData.event) }))
    submitStatus.current = null
    patch(`/dashboard/events/${event.id}`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: (page) => {
        const nextItems = (page.props as { purchasable_items?: EventFormPurchasableItem[] }).purchasable_items
        if (nextItems) setPurchasableItemRows(buildPurchasableItemRows(nextItems))
        if (previewWindow) previewWindow.location.href = `/events/${event.id}/preview`
      },
      onError: () => previewWindow?.close(),
      onFinish: () => { submitStatus.current = null },
    })
  }

  function buildPayload(source: EventFormEventData) {
    // Status is owned by the publish/unpublish toggle on the org edit page, so we
    // omit it there to avoid a stale form value clobbering a just-toggled state.
    // The Publish button still drives status on create and in the admin editor.
    const { hashtags_input: _, status, ...rest } = source
    const payload: Record<string, unknown> = {
      ...rest,
      hashtags: source.hashtags_input.split(',').map((tag) => tag.trim()).filter(Boolean),
      sponsors: source.sponsors.map((sponsor) => serializeSponsor(sponsor)),
      dress_code: source.dress_code || null,
      primary_cause: source.primary_cause || null,
      causes: source.causes,
      starting_ticket_price: source.starting_ticket_price || null,
    }
    if (isAdmin || !isEdit) payload.status = submitStatus.current ?? status

    if (showNativeItemBuilder) {
      payload.purchasable_items_attributes = buildPurchasableItemsAttributes(purchasableItemRows)
    }
    if (payload.page_kind === 'fundraiser') normalizeFundraiserPayload(payload)
    return payload
  }

  const previewEvent = transformForPreview(data.event, coverImagePreview)
  const sectionTitles: Partial<Record<EditTab, string>> = {
    selling: 'Selling',
    sponsors: 'Sponsors',
  }
  const formTitle = isEdit
    ? (tabbedEdit ? sectionTitles[activeEditTab] ?? 'Campaign details' : 'Campaign details')
    : 'Create Campaign'
  const richSections = (sections: RichSectionKey[]) => (
    <EventFormRichSections
      value={data.event}
      onChange={(next) => {
        const rich = typeof next === 'function' ? next(data.event) : next
        setEvent({ ...data.event, ...rich })
      }}
      pro={isPro}
      sections={sections}
    />
  )
  const detailsFields = (
    <>
      {!isEdit && <PageTypeSection value={data.event} onChange={setEvent} />}
      <GeneralInfoSection value={data.event} onChange={setEvent} fieldError={fieldError} />
      <CoverImageSection
        currentUrl={event?.cover_image_url}
        value={data.event}
        onChange={setEvent}
        onPreviewChange={setCoverImagePreview}
      />
      {!isFundraiser && <DateVenueSection value={data.event} onChange={setEvent} fieldError={fieldError} />}
      {isFundraiser ? (
        <FundraisingSection
          value={data.event}
          onChange={setEvent}
          fieldError={fieldError}
          stripePaymentReady={stripePaymentReady}
          isAdmin={isAdmin}
          isPro={isPro}
        />
      ) : (
        <EventSettingsSection
          value={data.event}
          onChange={setEvent}
          fieldError={fieldError}
          dressCodes={dressCodes}
          causes={causes}
          isAdmin={isAdmin}
        />
      )}
    </>
  )
  const ticketingSection = showTicketingSection ? (
    <TicketingPaymentsSection
      value={data.event}
      onChange={setEvent}
      rows={purchasableItemRows}
      setRows={setPurchasableItemRows}
      stripePaymentReady={stripePaymentReady}
      fieldError={fieldError}
      showNativeTiers={!isAdmin}
      showExternalPrice={!isAdmin}
    />
  ) : null
  const sellingFields = showNativeSelling ? (
    <SellingSections
      activeTab={activeSellingTab}
      onTabChange={setActiveSellingTab}
      rows={purchasableItemRows}
      setRows={setPurchasableItemRows}
      stripePaymentReady={stripePaymentReady}
      fieldError={fieldError}
      value={data.event}
      onChange={setEvent}
    />
  ) : null
  const untabbedFields = (
    <>
      {detailsFields}
      {ticketingSection}
      {sellingFields}
      <AppearanceSection value={data.event} onChange={setEvent} fieldError={fieldError} />
      {!isFundraiser && richSections(['hosts', 'honorees', 'entertainment', 'sponsors', 'event_links', 'committee'])}
    </>
  )
  const standaloneSection: Partial<Record<EditTab, ReactNode>> = {
    selling: <div className="space-y-6 pt-2">{ticketingSection}{sellingFields}</div>,
    sponsors: <div className="space-y-6 pt-2">{richSections(['sponsors'])}</div>,
  }
  const tabbedFields = !isFundraiser && standaloneSection[activeEditTab] ? (
    standaloneSection[activeEditTab]
  ) : (
    <CampaignEditorTabs
      activeTab={activeEditTab}
      onTabChange={(tab) => {
        setActiveEditTab(tab)
        window.history.replaceState(null, '', `#${tab}`)
      }}
      fundraiser={isFundraiser}
    >
      <TabsContent value="details" className="space-y-6 pt-4">
        {detailsFields}
      </TabsContent>
      <TabsContent value="appearance" className="space-y-6 pt-4">
        <AppearanceSection value={data.event} onChange={setEvent} fieldError={fieldError} />
      </TabsContent>
      {!isFundraiser && (
        <>
          <TabsContent value="hosts" className="space-y-6 pt-4">
            {richSections(['hosts', 'honorees', 'committee'])}
          </TabsContent>
          <TabsContent value="entertainment" className="space-y-6 pt-4">
            {richSections(['entertainment'])}
          </TabsContent>
          <TabsContent value="links" className="space-y-6 pt-4">
            {richSections(['event_links'])}
          </TabsContent>
        </>
      )}
    </CampaignEditorTabs>
  )
  const formBody = (
    <form id="campaign-form" onSubmit={handleSubmit} className="space-y-6 pb-24">
      {fieldError('base') && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-base text-destructive">
          {fieldError('base')}
        </div>
      )}
      {tabbedEdit ? tabbedFields : untabbedFields}
      <FormActions
        isEdit={isEdit}
        processing={processing}
        cancelHref={actionUrl || '/dashboard/events'}
        onSave={() => { submitStatus.current = null }}
      />
    </form>
  )
  const form = useFullPagePreview ? (
    formBody
  ) : (
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      {formBody}
      <PreviewButton />
      <PreviewDialog
        event={previewEvent}
        organizationName={current_organization?.name ?? 'Your Organization'}
        coverImagePreview={coverImagePreview}
      />
    </Dialog>
  )

  if (isEdit && event && !isAdmin) {
    return (
      <CampaignWorkspace
        event={{ ...event, landing_url: landingUrl }}
        title={formTitle}
        active="edit"
        actions={(
          <>
            <Button type="button" variant="outline" onClick={handlePreview} disabled={processing}>
              <Eye className="size-4" /> Preview
            </Button>
            <Button
              type="submit"
              form="campaign-form"
              variant="outline"
              disabled={processing}
              onClick={() => { submitStatus.current = null }}
            >
              Save changes
            </Button>
          </>
        )}
      >
        <div className="max-w-4xl">{form}</div>
        <SavedDialog open={savedOpen} onOpenChange={setSavedOpen} title="Campaign saved" />
      </CampaignWorkspace>
    )
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-semibold tracking-tight">{formTitle}</h1>
        <TopActions
          isEdit={isEdit}
          processing={processing}
          onPreview={() => setPreviewOpen(true)}
          onSave={() => { submitStatus.current = null }}
          onPublish={() => { submitStatus.current = 'published' }}
        />
      </div>
      {form}
    </div>
  )
}

function buildPurchasableItemRows(items: EventFormPurchasableItem[]): PurchasableItemRowState[] {
  return items.map((item) => newPurchasableItemRow(item.kind ?? 'ticket', item))
}

function getSellingTabForErrors(
  pageErrors: Record<string, string | string[]>,
  rows: PurchasableItemRowState[],
  fallbackTab: SellingTab,
): SellingTab | null {
  if (pageErrors.donations_enabled || pageErrors.show_donation_progress || pageErrors.donation_goal_cents) {
    return 'donations'
  }

  const rowErrorId = Object.keys(pageErrors)
    .map((key) => /^purchasable_items\.([^.]+)\./.exec(key)?.[1])
    .find((id): id is string => !!id)
  if (rowErrorId) {
    const row = rows.find((candidate) => candidate.id === rowErrorId)
    return row ? sellingTabForKind(row.kind) : fallbackTab
  }

  return Object.keys(pageErrors).some((key) => key.startsWith('purchasable_items.'))
    ? fallbackTab
    : null
}

function sellingTabForKind(kind: PurchasableItemRowState['kind']): SellingTab {
  if (kind === 'sponsorship') return 'sponsorships'
  if (kind === 'ad') return 'ads'
  // Ticket tiers live outside the selling tabs now; fall back to a valid tab so
  // the surrounding "Selling" edit tab still opens to reveal the ticket card.
  return 'sponsorships'
}

function FormActions({
  isEdit,
  processing,
  cancelHref,
  onSave,
}: {
  isEdit: boolean
  processing: boolean
  cancelHref: string
  onSave: () => void
}) {
  return (
    <div className="flex flex-wrap justify-end gap-3">
      <Button type="button" variant="outline" asChild>
        <a href={cancelHref}>Cancel</a>
      </Button>
      <Button type="submit" variant="outline" disabled={processing} onClick={onSave}>
        {isEdit ? 'Save changes' : 'Save draft'}
      </Button>
    </div>
  )
}

function TopActions({
  isEdit,
  processing,
  onPreview,
  onSave,
  onPublish,
}: {
  isEdit: boolean
  processing: boolean
  onPreview: () => void
  onSave: () => void
  onPublish: () => void
}) {
  return (
    <>
      <Button type="button" variant="outline" onClick={onPreview}>
        <Eye className="size-4" /> Preview
      </Button>
      <Button type="submit" form="campaign-form" variant="outline" disabled={processing} onClick={onSave}>
        {isEdit ? 'Save changes' : 'Save draft'}
      </Button>
      <Button type="submit" form="campaign-form" disabled={processing} onClick={onPublish}>
        Publish
      </Button>
    </>
  )
}

function normalizeFundraiserPayload(payload: Record<string, unknown>) {
  const external = payload.payment_mode === 'external'
  Object.assign(payload, {
    date: null,
    start_time: null,
    end_time: null,
    venue_name: null,
    street_address: null,
    city: null,
    state: null,
    zip: null,
    dress_code: null,
    starting_ticket_price: null,
    purchase_button_text: null,
    auction_url: null,
    raffle_url: null,
    countdown_timer: false,
    auction_items: false,
    gift_items: false,
  })
  // External fundraisers keep their linked page in `ticket_link`; native ones drop it.
  if (external) {
    payload.donations_enabled = false
  } else {
    payload.ticket_link = null
  }
  delete payload.purchasable_items_attributes
}
